import { type AgentDto, can } from '@af/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Dialog as RDialog } from 'radix-ui';
import { type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AgentOrb } from '@/components/ui/agent-orb';
import {
  IconBell,
  IconBook,
  IconBuilding,
  IconChart,
  IconChat,
  IconCheck,
  IconFlag,
  IconHome,
  IconKeyboard,
  IconLanguage,
  IconMoon,
  IconOffice,
  IconPause,
  IconPlay,
  IconPlus,
  IconSearch,
  IconSettings,
  IconSparkle,
  IconStop,
  IconTasks,
  IconUsers,
} from '@/components/ui/icons';
import { usePaneActions } from '@/components/workspace/actions';
import { useWorkspace } from '@/components/workspace/store';
import { setLanguage } from '@/i18n';
import { call } from '@/lib/api';
import { SETTINGS_SECTIONS, type SettingsSection } from '@/lib/constants';
import { relTime, usd } from '@/lib/format';
import { deriveOnboarding } from '@/lib/onboarding';
import { openHalt } from '@/lib/palette';
import { canCreateCompany, useRole } from '@/lib/permissions';
import {
  qk,
  useAgents,
  useApiMutation,
  useCompanies,
  useMe,
  useOnboardingStatus,
  useOrgStatus,
} from '@/lib/queries';
import { liveRunStore } from '@/lib/run-resume';
import { EXTRA_GO, MOD } from '@/lib/shortcuts';
import { THEME_PREFS, useTheme } from '@/lib/theme';
import { cn, IS_MOCK } from '@/lib/utils';
import {
  byGroupOrder,
  type LauncherIcon,
  type LauncherItem,
  loadRecent,
  parseQuery,
  pushRecent,
  resolveAgent,
  searchItems,
} from './launcher-core';
import { NAV } from './nav';
import { useSwitchOrg } from './UserMenu';

type IconC = (p: { size?: number; className?: string }) => ReactNode;
const ICONS: Record<LauncherIcon, IconC> = {
  home: IconHome,
  company: IconBuilding,
  agents: IconUsers,
  chat: IconChat,
  tasks: IconTasks,
  runs: IconPlay,
  approvals: IconFlag,
  usage: IconChart,
  notifications: IconBell,
  settings: IconSettings,
  office: IconOffice,
  plus: IconPlus,
  stop: IconStop,
  play: IconPlay,
  theme: IconMoon,
  org: IconBuilding,
  keyboard: IconKeyboard,
  sparkle: IconSparkle,
  help: IconBook,
  agent: IconUsers,
  pause: IconPause,
  lang: IconLanguage,
  check: IconCheck,
};
const NAV_ICON: Record<string, LauncherIcon> = {
  '/': 'home',
  '/chat': 'company',
  '/master': 'sparkle',
  '/tasks': 'tasks',
  '/runs': 'runs',
  '/approvals': 'approvals',
  '/usage': 'usage',
  '/run-analysis': 'usage',
  '/notifications': 'notifications',
  '/help': 'help',
  '/settings': 'settings',
};
const SETTINGS_PERM: Partial<Record<SettingsSection, Parameters<typeof can>[1]>> = {
  providers: 'provider:read',
  models: 'provider:read',
  budgets: 'budget:read',
  members: 'member:read',
  audit: 'audit:read',
  mcp: 'token:read',
};

function Keycap({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded-md border border-border bg-panel px-1 font-sans text-[11px] text-muted">
      {children}
    </kbd>
  );
}

/** Data + actions → a flat, searchable index. Built only while the launcher is open. */
function useLauncherIndex(
  open: boolean,
  ctl: { close: () => void; setInput: (v: string) => void; onShortcuts: () => void },
) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const role = useRole();
  const theme = useTheme();
  const agents = useAgents();
  const status = useOrgStatus();
  const orgs = useCompanies();
  const onboarding = useOnboardingStatus(open);
  const me = useMe();
  const switchOrg = useSwitchOrg(me.data?.organization?.id);
  const has = (p: Parameters<typeof can>[1]) => can(role, p);
  const openPanes = useWorkspace().state.panes.length;
  const { closePanes } = usePaneActions();
  const conversations = useQuery({
    queryKey: qk.conversations(),
    queryFn: () => call('listConversations', { query: {} }),
    enabled: open && has('conversation:read'),
  });
  const runs = useQuery({
    queryKey: qk.runs(),
    queryFn: () => call('listRuns', { query: undefined }),
    enabled: open && has('run:read'),
  });
  const tasks = useQuery({
    queryKey: qk.tasks,
    queryFn: () => call('listTasks', { query: { limit: 200 } }),
    enabled: open && has('task:read'),
  });
  const approvals = useQuery({
    queryKey: qk.approvals('pending'),
    queryFn: () => call('listApprovals', { query: { status: 'pending' } }),
    enabled: open && has('approval:read'),
  });
  const pause = useApiMutation('updateAgent', { invalidate: [qk.agents] });
  const resume = useApiMutation('orgResume', {
    invalidate: [qk.orgStatus, qk.agents],
    success: 'Organization resumed',
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: rebuilt from query data + prefs only
  return useMemo(() => {
    if (!open)
      return { items: [] as LauncherItem[], agents: [] as AgentDto[], byId: new Map<string, LauncherItem>() };
    const go = (to: string, search?: Record<string, unknown>, params?: Record<string, string>) => () =>
      // biome-ignore lint/suspicious/noExplicitAny: dynamic launcher destinations
      void navigate({ to: to as any, search: search as any, params: params as any });
    const list = (agents.data ?? []).filter((a) => a.status !== 'archived');
    const agentName = (id: string) => list.find((a) => a.id === id)?.name ?? 'agent';
    const items: LauncherItem[] = [];

    // Setup
    if (onboarding.data) {
      const p = deriveOnboarding(onboarding.data);
      if (!p.complete)
        items.push({
          id: 'setup:continue',
          group: 'setup',
          icon: 'sparkle',
          title: t('onboarding.continue'),
          subtitle: t('onboarding.progress', { done: p.done, total: p.total }),
          keywords: 'onboarding getting started setup checklist',
          hint: 'g i',
          command: true,
          run: go('/onboarding', { step: p.next ?? undefined }),
        });
    }

    // Navigation
    for (const n of NAV) {
      if (n.perm && !has(n.perm)) continue;
      items.push({
        id: `nav:${n.to}`,
        group: 'navigate',
        icon: NAV_ICON[n.to] ?? 'home',
        title: t(n.key),
        keywords: `go ${n.to}`,
        hint: `g ${n.hotkey}`,
        run: go(n.to),
      });
    }
    for (const g of EXTRA_GO) {
      items.push({
        id: `nav:${g.to}:${g.hotkey}`,
        group: 'navigate',
        icon: 'sparkle',
        title: t(g.label),
        keywords: 'onboarding setup getting started',
        hint: `g ${g.hotkey}`,
        run: go(g.to, g.search),
      });
    }

    // Settings sections
    for (const s of SETTINGS_SECTIONS) {
      const perm = SETTINGS_PERM[s];
      if (perm && !has(perm)) continue;
      items.push({
        id: `settings:${s}`,
        group: 'settings',
        icon: 'settings',
        title: t(`settings.${s}`),
        subtitle: t('nav.settings'),
        keywords: `settings ${s}`,
        run: go('/settings/$section', undefined, { section: s }),
      });
    }

    // Agents (Tab → actions)
    const agentActions = (a: AgentDto): LauncherItem[] => {
      const out: LauncherItem[] = [];
      if (has('conversation:read'))
        out.push({
          id: `agent:${a.id}:chat`,
          group: 'actions',
          icon: 'chat',
          title: t('palette.chatWith', { name: a.name }),
          run: go('/chat', { agent: a.id }),
        });
      if (has('conversation:create') && has('agent:run'))
        out.push({
          id: `agent:${a.id}:ask`,
          group: 'actions',
          icon: 'sparkle',
          title: t('launcher.askAgent', { name: a.name }),
          subtitle: `@${a.slug} …`,
          run: () => ctl.setInput(`@${a.slug} `),
        });
      if (has('agent:update'))
        out.push({
          id: `agent:${a.id}:pause`,
          group: 'actions',
          icon: a.status === 'paused' ? 'play' : 'pause',
          title: a.status === 'paused' ? t('ops.resume', { name: a.name }) : t('ops.pause', { name: a.name }),
          run: () =>
            pause.mutate(
              { params: { id: a.id }, body: { status: a.status === 'paused' ? 'active' : 'paused' } },
              {
                onSuccess: () =>
                  toast.success(a.status === 'paused' ? `${a.name} resumed` : `${a.name} paused`),
              },
            ),
        });
      if (has('run:read'))
        out.push({
          id: `agent:${a.id}:runs`,
          group: 'actions',
          icon: 'runs',
          title: t('launcher.openRuns', { name: a.name }),
          run: go('/runs', { agent: a.id }),
        });
      out.push({
        id: `agent:${a.id}:open`,
        group: 'actions',
        icon: 'agent',
        title: t('launcher.openAgent', { name: a.name }),
        run: go('/agents/$id', undefined, { id: a.id }),
      });
      return out;
    };
    if (has('agent:read'))
      for (const a of list)
        items.push({
          id: `agent:${a.id}`,
          group: 'agents',
          icon: 'agent',
          orb: a.id,
          title: a.name,
          subtitle: `${a.roleTitle} · @${a.slug}${a.status === 'paused' ? ' · paused' : ''}`,
          keywords: `${a.slug} ${a.roleTitle} agent`,
          hint: 'Tab',
          agentSlug: a.slug,
          run: go('/agents/$id', undefined, { id: a.id }),
          secondary: has('conversation:read')
            ? { label: t('launcher.chat'), run: go('/chat', { agent: a.id }) }
            : undefined,
          drill: () => agentActions(a),
        });

    // Conversations / runs / tasks / approvals
    for (const c of (conversations.data ?? []).slice(0, 40))
      items.push({
        id: `conv:${c.id}`,
        group: 'conversations',
        icon: 'chat',
        title: c.title,
        subtitle: `${agentName(c.agentId)} · ${relTime(c.updatedAt)}`,
        keywords: 'conversation chat',
        run: go('/chat/$conversationId', undefined, { conversationId: c.id }),
        secondary: {
          label: t('launcher.openAgent', { name: agentName(c.agentId) }),
          run: go('/agents/$id', undefined, { id: c.agentId }),
        },
      });
    for (const r of (runs.data ?? []).slice(0, 30))
      items.push({
        id: `run:${r.id}`,
        group: 'runs',
        icon: 'runs',
        title: `${agentName(r.agentId)} · ${r.mode} ${r.status.replace(/_/g, ' ')}`,
        subtitle: `${r.id} · ${usd(r.costUsd)} · ${relTime(r.startedAt ?? r.createdAt)}`,
        keywords: `run ${r.id} ${r.status}`,
        run: go('/runs/$id', undefined, { id: r.id }),
        secondary: {
          label: t('launcher.openAgent', { name: agentName(r.agentId) }),
          run: go('/agents/$id', undefined, { id: r.agentId }),
        },
      });
    for (const tk of (tasks.data ?? []).slice(0, 40))
      items.push({
        id: `task:${tk.id}`,
        group: 'tasks',
        icon: 'tasks',
        title: tk.title,
        subtitle: `${tk.status.replace(/_/g, ' ')} · ${agentName(tk.assigneeAgentId)}`,
        keywords: 'task',
        run: tk.runId ? go('/runs/$id', undefined, { id: tk.runId }) : go('/tasks'),
        secondary: { label: t('nav.tasks'), run: go('/tasks') },
      });
    for (const ap of approvals.data ?? [])
      items.push({
        id: `approval:${ap.id}`,
        group: 'approvals',
        icon: 'approvals',
        title: `${ap.toolName} · ${agentName(ap.agentId)}`,
        subtitle: ap.inputPreview,
        keywords: 'approval pending approve deny',
        run: go('/approvals'),
        secondary: { label: t('launcher.openRun'), run: go('/runs/$id', undefined, { id: ap.runId }) },
      });

    // Actions (also the `>` command set)
    const action = (i: Omit<LauncherItem, 'group' | 'command'>) =>
      items.push({ ...i, group: 'actions', command: true });
    if (has('agent:create')) {
      action({
        id: 'act:new-agent',
        icon: 'plus',
        title: t('palette.newAgent'),
        keywords: 'create add agent',
        run: go('/agents/new'),
      });
      action({
        id: 'act:agent-template',
        icon: 'sparkle',
        title: t('launcher.agentFromTemplate'),
        keywords: 'template ceo manager researcher developer assistant',
        run: go('/onboarding', { step: 'agent' }),
      });
    }
    if (has('task:create'))
      action({
        id: 'act:new-task',
        icon: 'plus',
        title: t('palette.newTask'),
        keywords: 'create add task',
        run: go('/tasks', { new: true }),
      });
    if (has('conversation:create'))
      action({
        id: 'act:new-chat',
        icon: 'chat',
        title: t('palette.newChat'),
        keywords: 'chat conversation',
        run: go('/chat'),
      });
    if (has('org:halt')) {
      if (status.data?.agentsHalted)
        action({
          id: 'act:resume',
          icon: 'play',
          title: t('launcher.resumeAll'),
          keywords: 'resume unhalt start',
          run: () => resume.mutate({ body: {} }),
        });
      else
        action({
          id: 'act:halt',
          icon: 'stop',
          title: t('shell.killTitle'),
          keywords: 'halt kill stop emergency',
          run: openHalt,
        });
    }
    action({
      id: 'act:toggle-theme',
      icon: 'theme',
      title: t('launcher.toggleTheme'),
      subtitle: t(`theme.${theme.theme}`),
      keywords: 'theme dark light appearance',
      run: theme.toggle,
    });
    for (const p of THEME_PREFS)
      action({
        id: `act:theme-${p}`,
        icon: 'theme',
        title: t('palette.theme', { name: t(`theme.${p}`) }),
        keywords: `theme ${p} appearance`,
        hint: theme.pref === p ? '✓' : undefined,
        run: () => theme.setPref(p),
      });
    action({
      id: 'act:terminal-dark',
      icon: 'theme',
      title: t('palette.terminalDark'),
      keywords: 'terminal panes dark',
      hint: theme.terminalDark ? '✓' : undefined,
      run: () => theme.setTerminalDark(!theme.terminalDark),
    });
    if (openPanes > 0)
      action({
        id: 'act:close-panes',
        icon: 'stop',
        title: t('palette.closePanes'),
        subtitle: String(openPanes),
        keywords: 'close all panes chats runs tabs',
        run: () => closePanes(() => true),
      });
    action({
      id: 'act:lang',
      icon: 'lang',
      title: t('palette.toggleLang'),
      keywords: 'language nederlands english',
      run: () => setLanguage(i18n.language === 'nl' ? 'en' : 'nl'),
    });
    for (const o of (orgs.data ?? []).filter((x) => x.id !== me.data?.organization?.id))
      action({
        id: `act:org:${o.id}`,
        icon: 'org',
        title: t('launcher.switchOrg', { name: o.name }),
        keywords: 'switch organization workspace org',
        run: () => void switchOrg(o.id),
      });
    action({
      id: 'act:companies',
      icon: 'org',
      title: t('companies.all'),
      keywords: 'companies organizations workspaces switch overview',
      run: go('/companies'),
    });
    if (canCreateCompany(me.data))
      action({
        id: 'act:new-company',
        icon: 'org',
        title: t('companies.new'),
        keywords: 'create add company organization workspace',
        run: go('/companies', { new: true }),
      });
    action({
      id: 'act:shortcuts',
      icon: 'keyboard',
      title: t('nav.shortcuts'),
      keywords: 'help keys hotkeys ?',
      hint: '?',
      run: ctl.onShortcuts,
    });

    if (IS_MOCK) {
      const mock = (fn: (m: typeof import('@/mock')) => void) => () =>
        void import('@/mock').then((m) => {
          fn(m);
          toast.info('Mock updated — reloading');
          setTimeout(() => location.reload(), 300);
        });
      const dev = (i: Omit<LauncherItem, 'group' | 'command' | 'icon'>) =>
        items.push({ ...i, icon: 'settings', group: 'dev', command: true });
      for (const r of ['owner', 'admin', 'member', 'viewer'] as const)
        dev({ id: `dev:role:${r}`, title: `mock: act as ${r}`, run: mock((m) => m.mockControls.setRole(r)) });
      dev({
        id: 'dev:reset',
        title: 'mock: reset demo data',
        run: mock((m) => m.mockControls.reset('seeded')),
      });
      dev({
        id: 'dev:blank',
        title: 'mock: blank workspace (guided setup)',
        run: mock((m) => m.blankWorkspace()),
      });
      dev({
        id: 'dev:first-run',
        title: 'mock: reset to first-run (/setup)',
        run: mock((m) => m.mockControls.reset('empty')),
      });
    }

    return { items, agents: list, byId: new Map(items.map((i) => [i.id, i])) };
  }, [
    open,
    agents.data,
    conversations.data,
    runs.data,
    tasks.data,
    approvals.data,
    orgs.data,
    me.data?.organization?.id,
    onboarding.data,
    status.data?.agentsHalted,
    theme.pref,
    theme.theme,
    theme.terminalDark,
    role,
    i18n.language,
    openPanes,
    closePanes,
  ]);
}

/**
 * Spotlight/Raycast-style launcher (⌘K / Ctrl+K, `/`, ⌘J for "Ask an agent").
 * Search is deterministic and local: a fuzzy index over navigation, agents, conversations, runs,
 * tasks, approvals, settings and actions. `>` = command mode, `@agent message` = send to that agent.
 */
export function CommandPalette({
  open,
  onOpenChange,
  initialInput = '',
  onShortcuts,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  initialInput?: string;
  onShortcuts: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const role = useRole();
  const status = useOrgStatus();
  const [input, setInput] = useState(initialInput);
  const [active, setActive] = useState(0);
  const [drill, setDrill] = useState<LauncherItem | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const create = useApiMutation('createConversation', { invalidate: [['conversations']] });
  const send = useApiMutation('sendMessage');

  useEffect(() => {
    if (open) {
      setInput(initialInput);
      setDrill(null);
      setActive(0);
    }
  }, [open, initialInput]);

  const close = () => onOpenChange(false);
  const index = useLauncherIndex(open, {
    close,
    setInput: (v) => {
      setDrill(null);
      setInput(v);
      requestAnimationFrame(() => inputRef.current?.focus());
    },
    onShortcuts: () => {
      close();
      onShortcuts();
    },
  });

  const parsed = parseQuery(input);
  const sendTo = async (agent: AgentDto, text: string) => {
    if (status.data?.agentsHalted) return toast.error(t('home.halted'));
    if (!can(role, 'conversation:create') || !can(role, 'agent:run')) return toast.error(t('home.readOnly'));
    close();
    try {
      const c = await create.mutateAsync({ body: { agentId: agent.id, title: text.slice(0, 48) } });
      const r = await send.mutateAsync({ params: { id: c.id }, body: { text } });
      liveRunStore.set(c.id, r.runId);
      void qc.invalidateQueries({ queryKey: qk.messages(c.id) });
      void navigate({ to: '/chat/$conversationId', params: { conversationId: c.id } });
    } catch {
      /* the mutation already toasted */
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: derived from input + index only
  const results: LauncherItem[] = useMemo(() => {
    if (drill) return searchItems(drill.drill?.() ?? [], input, 20);
    if (parsed.mode === 'command')
      return searchItems(
        index.items.filter((i) => i.command),
        parsed.q,
        40,
      );
    if (parsed.mode === 'mention') {
      const target = parsed.hasSpace ? resolveAgent(parsed.handle, index.agents) : undefined;
      const candidates = searchItems(
        index.items.filter((i) => i.group === 'agents'),
        parsed.handle,
        8,
      ).map(
        (i): LauncherItem => ({
          ...i,
          hint: 'Tab',
          // Enter / Tab on an agent completes the handle; the message follows.
          run: () => setInput(`@${i.agentSlug} `),
          drill: undefined,
          secondary: undefined,
        }),
      );
      if (target && parsed.message) {
        return [
          {
            id: `send:${target.id}`,
            group: 'send',
            icon: 'sparkle',
            orb: target.id,
            title: t('launcher.sendTo', { name: target.name }),
            subtitle: `“${parsed.message}”`,
            hint: '⏎',
            run: () => void sendTo(target, parsed.message),
          },
        ];
      }
      return candidates;
    }
    if (!parsed.q) {
      const recent = loadRecent()
        .map((id) => index.byId.get(id))
        .filter((i): i is LauncherItem => !!i)
        .map((i) => ({ ...i, group: 'recent' as const }));
      const recentIds = new Set(recent.map((r) => r.id));
      const suggested = index.items.filter(
        (i) =>
          !recentIds.has(i.id) &&
          (i.group === 'setup' ||
            i.group === 'navigate' ||
            i.group === 'agents' ||
            i.id === 'act:new-agent' ||
            i.id === 'act:new-task'),
      );
      return byGroupOrder([...recent, ...suggested]);
    }
    return searchItems(index.items, parsed.q);
  }, [index, input, drill]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset the selection when the query changes
  useEffect(() => setActive(0), [input, drill]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on selection change
  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const execute = (item: LauncherItem | undefined, secondary = false) => {
    if (!item) return;
    const action = secondary ? item.secondary?.run : item.run;
    if (!action) return;
    const keepOpen = item.id.startsWith('agent:') && item.id.endsWith(':ask');
    const completing = parsed.mode === 'mention' && item.group === 'agents';
    if (!item.id.startsWith('send:') && !completing) pushRecent(drill ? drill.id : item.id);
    if (!keepOpen && !completing) close();
    action();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const n = results.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (n ? (a + 1) % n : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (n ? (a - 1 + n) % n : 0));
    } else if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      e.preventDefault();
      execute(results[active], e.metaKey || e.ctrlKey);
    } else if (e.key === 'Tab' && !e.shiftKey) {
      const item = results[active];
      if (!item) return;
      if (parsed.mode === 'mention' && item.agentSlug) {
        e.preventDefault();
        setInput(`@${item.agentSlug} `);
      } else if (item.drill) {
        e.preventDefault();
        setDrill(item);
        setInput('');
      }
    } else if (e.key === 'Backspace' && drill && input === '') {
      e.preventDefault();
      setDrill(null);
    }
  };

  const activeItem = results[active];
  const modeChip = drill ? (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-panel-2 px-2.5 py-0.5 text-xs font-medium text-fg">
      {drill.orb ? <AgentOrb seed={drill.orb} size={14} /> : null}
      {drill.title} ›
    </span>
  ) : parsed.mode === 'command' ? (
    <span className="shrink-0 rounded-md bg-accent/15 px-2.5 py-0.5 text-xs font-medium text-accent">
      {t('launcher.commands')}
    </span>
  ) : parsed.mode === 'mention' ? (
    <span className="shrink-0 rounded-md bg-claude/15 px-2.5 py-0.5 text-xs font-medium text-claude">
      {t('launcher.askMode')}
    </span>
  ) : null;

  // Render rows with group headings.
  const rows: ReactNode[] = [];
  let lastGroup = '';
  results.forEach((item, i) => {
    if (item.group !== lastGroup) {
      lastGroup = item.group;
      rows.push(
        <div
          key={`h-${item.group}-${item.id}`}
          role="presentation"
          className="px-3 pt-3 pb-1 text-xs font-medium text-faint"
        >
          {drill ? t('launcher.actionsFor', { name: drill.title }) : t(`launcher.group.${item.group}`)}
        </div>,
      );
    }
    const Icon = ICONS[item.icon];
    const selected = i === active;
    rows.push(
      // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard handled on the combobox input
      <div
        key={item.id}
        id={`launcher-opt-${i}`}
        role="option"
        aria-selected={selected}
        tabIndex={-1}
        onMouseMove={() => setActive(i)}
        onClick={() => execute(item)}
        className={cn(
          'flex h-11 touch:h-14 cursor-pointer items-center gap-3 rounded-lg px-3 text-sm',
          selected ? 'bg-hover text-fg' : 'text-fg-2',
        )}
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-bg text-muted">
          {item.orb ? <AgentOrb seed={item.orb} size={16} /> : <Icon size={15} />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-fg">{item.title}</span>
          {item.subtitle ? <span className="block truncate text-xs text-muted">{item.subtitle}</span> : null}
        </span>
        {item.hint ? <span className="shrink-0 font-mono text-2xs text-faint">{item.hint}</span> : null}
        {selected ? (
          <span className="touch:hidden">
            <Keycap>⏎</Keycap>
          </span>
        ) : null}
        {/* Touch has no ⌘⏎ / Tab: the same two actions as explicit buttons. */}
        {item.secondary ? (
          <button
            type="button"
            aria-label={item.secondary.label}
            title={item.secondary.label}
            onClick={(e) => {
              e.stopPropagation();
              execute(item, true);
            }}
            className="hidden h-9 shrink-0 items-center rounded-lg border border-border px-3 text-xs text-fg-2 touch:inline-flex"
          >
            {item.secondary.label}
          </button>
        ) : null}
        {item.drill && parsed.mode !== 'mention' ? (
          <button
            type="button"
            aria-label={t('launcher.hintDrill')}
            title={t('launcher.hintDrill')}
            onClick={(e) => {
              e.stopPropagation();
              setDrill(item);
              setInput('');
            }}
            className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border text-fg-2 touch:inline-flex"
          >
            ›
          </button>
        ) : null}
      </div>,
    );
  });

  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-50 bg-[var(--overlay)] backdrop-blur-[2px]" />
        <RDialog.Content
          aria-describedby={undefined}
          onEscapeKeyDown={(e) => {
            // Esc leaves a drill-down first; a second Esc closes the launcher.
            if (drill) {
              e.preventDefault();
              setDrill(null);
              setInput('');
            }
          }}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
          className="fixed top-[12dvh] left-1/2 z-50 flex max-h-[76dvh] max-sm:top-2 max-sm:max-h-[calc(100dvh-1rem)] w-[calc(100vw-24px)] max-w-[680px] -translate-x-1/2 flex-col overflow-hidden rounded-2xl border border-border bg-bg shadow-pop"
        >
          <RDialog.Title className="sr-only">{t('shell.commandPalette')}</RDialog.Title>
          <div className="flex h-16 shrink-0 items-center gap-3 border-b border-border px-4">
            <IconSearch size={20} className="shrink-0 text-muted" />
            {modeChip}
            <input
              ref={inputRef}
              role="combobox"
              aria-expanded
              aria-controls="launcher-list"
              aria-activedescendant={activeItem ? `launcher-opt-${active}` : undefined}
              aria-label={t('shell.commandPalette')}
              aria-autocomplete="list"
              autoComplete="off"
              spellCheck={false}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={drill ? t('launcher.filterActions') : t('launcher.placeholder')}
              className="h-full min-w-0 flex-1 bg-transparent text-lg text-fg outline-none placeholder:text-faint touch:text-[16px]"
            />
            <span className="touch:hidden">
              <Keycap>Esc</Keycap>
            </span>
          </div>
          <div
            ref={listRef}
            id="launcher-list"
            role="listbox"
            aria-label={t('launcher.results')}
            className="min-h-0 flex-1 overflow-y-auto p-1.5"
          >
            {results.length ? (
              rows
            ) : (
              <div className="px-3 py-10 text-center text-sm text-muted">
                {parsed.mode === 'mention' && parsed.hasSpace && !resolveAgent(parsed.handle, index.agents)
                  ? t('launcher.noAgent', { handle: parsed.handle })
                  : parsed.mode === 'mention' && !parsed.message && parsed.hasSpace
                    ? t('launcher.typeMessage')
                    : t('palette.empty')}
              </div>
            )}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 border-t border-border bg-panel px-4 py-2 text-xs text-muted touch:hidden">
            <span className="flex items-center gap-1">
              <Keycap>↑</Keycap>
              <Keycap>↓</Keycap> {t('launcher.hintMove')}
            </span>
            <span className="flex items-center gap-1">
              <Keycap>⏎</Keycap> {t('launcher.hintOpen')}
            </span>
            {activeItem?.secondary ? (
              <span className="flex items-center gap-1">
                <Keycap>{MOD}</Keycap>
                <Keycap>⏎</Keycap> {activeItem.secondary.label}
              </span>
            ) : null}
            {activeItem?.drill && parsed.mode !== 'mention' ? (
              <span className="flex items-center gap-1">
                <Keycap>Tab</Keycap> {t('launcher.hintDrill')}
              </span>
            ) : null}
            <span className="ml-auto hidden items-center gap-1 sm:flex">
              <Keycap>&gt;</Keycap> {t('launcher.hintCommands')} <Keycap>@</Keycap> {t('launcher.hintAsk')}
            </span>
          </div>
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}
